import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Sun, Edit3, Download, Settings, Monitor, Smartphone, ArrowLeft, Send, History } from 'lucide-react';
import type { Draft } from '@shared/types';
import type { SaveStatus } from '../hooks/useDraft';

interface Props {
  draft: Draft;
  onRename: (name: string) => void;
  saveStatus: SaveStatus;
  onExport: () => void;
  onSend: () => void;
  previewMode: 'desktop' | 'mobile';
  setPreviewMode: (mode: 'desktop' | 'mobile') => void;
  onSettings: () => void;
  showSettings: boolean;
  onCoverage: () => void;
}

export default function EditorTopBar({
  draft, onRename, saveStatus, onExport, onSend, previewMode, setPreviewMode, onSettings, showSettings, onCoverage,
}: Props) {
  const [renaming, setRenaming] = useState(false);
  const [tempName, setTempName] = useState(draft.name);

  return (
    <div className="border-b border-slate-200 bg-white px-4 py-2.5 flex items-center gap-3 flex-shrink-0">
      <Link to="/" className="p-1.5 hover:bg-slate-100 rounded-md text-slate-500" title="Back to drafts">
        <ArrowLeft size={15} />
      </Link>
      <div className="flex items-center gap-2 pr-3 border-r border-slate-200 mr-1">
        <div className="w-7 h-7 bg-blue-100 rounded-md flex items-center justify-center">
          <Sun size={16} className="text-blue-700" />
        </div>
        <span className="font-display font-semibold text-slate-900 text-sm">Penobscot Morning</span>
      </div>

      {renaming ? (
        <input
          autoFocus
          value={tempName}
          onChange={(e) => setTempName(e.target.value)}
          onBlur={() => { onRename(tempName.trim() || 'Untitled'); setRenaming(false); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { onRename(tempName.trim() || 'Untitled'); setRenaming(false); }
            if (e.key === 'Escape') { setTempName(draft.name); setRenaming(false); }
          }}
          className="text-sm px-2 py-1 border border-blue-300 rounded bg-white outline-none ring-2 ring-blue-100 w-44"
        />
      ) : (
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-medium text-slate-900 truncate max-w-[280px]">{draft.name}</span>
          <button onClick={() => { setTempName(draft.name); setRenaming(true); }} className="p-1.5 hover:bg-slate-100 rounded-md text-slate-400 hover:text-slate-600" title="Rename draft">
            <Edit3 size={13} />
          </button>
        </div>
      )}

      <div className="text-xs text-slate-500 flex items-center gap-1.5 ml-2">
        {saveStatus === 'saving' && <><div className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-pulse" /> Saving…</>}
        {saveStatus === 'saved' && <><div className="w-1.5 h-1.5 bg-emerald-500 rounded-full" /> Saved</>}
        {saveStatus === 'idle' && <><div className="w-1.5 h-1.5 bg-slate-300 rounded-full" /> Up to date</>}
        {saveStatus === 'error' && <><div className="w-1.5 h-1.5 bg-red-500 rounded-full" /> Save failed</>}
        {saveStatus === 'conflict' && <span className="text-amber-700">⚠ Conflict — reload</span>}
      </div>

      <div className="flex-1" />

      <div className="flex items-center bg-slate-100 rounded-md p-0.5">
        <button onClick={() => setPreviewMode('desktop')} className={`p-1.5 rounded text-xs flex items-center gap-1 ${previewMode === 'desktop' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500'}`}>
          <Monitor size={13} /> Desktop
        </button>
        <button onClick={() => setPreviewMode('mobile')} className={`p-1.5 rounded text-xs flex items-center gap-1 ${previewMode === 'mobile' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500'}`}>
          <Smartphone size={13} /> Mobile
        </button>
      </div>

      <button onClick={onCoverage} className="px-2 py-1.5 hover:bg-slate-100 rounded-md text-slate-600 text-xs flex items-center gap-1.5" title="See what we've covered in the last 8 days">
        <History size={13} /> Last 8 days
      </button>

      <button onClick={onSettings} className={`p-2 hover:bg-slate-100 rounded-md ${showSettings ? 'bg-slate-100 text-slate-900' : 'text-slate-500'}`} title="Email settings">
        <Settings size={15} />
      </button>

      <button onClick={onExport} className="btn-secondary">
        <Download size={13} /> Export HTML
      </button>

      <button onClick={onSend} className="btn-primary">
        <Send size={13} /> Send
      </button>
    </div>
  );
}
